import { useRef, useState } from "react";
import { Camera, Upload, X } from "lucide-react";
import { fileUrl, itemsApi, Item, Photo } from "@/lib/catalog-api";

interface PhotoUploaderProps {
  itemId: string;
  photos: Photo[];
  onUpdated: (item: Item) => void;
  max?: number;
}

export function PhotoUploader({ itemId, photos, onUpdated, max = 10 }: PhotoUploaderProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cameraRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  async function uploadOne(file: File) {
    if (!file.type.startsWith("image/")) { setError("Only image files."); return; }
    if (file.size > 8 * 1024 * 1024) { setError("Photos must be under 8MB."); return; }
    setError(null); setBusy(true);
    try {
      const updated = await itemsApi.uploadPhoto(itemId, file);
      onUpdated(updated);
    } catch (e: any) {
      setError(e?.message || "Upload failed");
    } finally { setBusy(false); }
  }

  async function ingest(files: FileList | File[] | null) {
    if (!files) return;
    const arr = Array.from(files);
    for (const f of arr) {
      if (photos.length >= max) { setError(`Max ${max} photos.`); break; }
      // eslint-disable-next-line no-await-in-loop
      await uploadOne(f);
    }
  }

  async function remove(photoId: string) {
    setBusy(true);
    try { onUpdated(await itemsApi.deletePhoto(itemId, photoId)); }
    finally { setBusy(false); }
  }
  async function setPrimary(photoId: string) {
    setBusy(true);
    try { onUpdated(await itemsApi.setPrimaryPhoto(itemId, photoId)); }
    finally { setBusy(false); }
  }

  function onDragOver(e: React.DragEvent) {
    e.preventDefault();
    if (!dragActive) setDragActive(true);
  }
  function onDragLeave(e: React.DragEvent) {
    e.preventDefault();
    setDragActive(false);
  }
  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      ingest(e.dataTransfer.files);
    }
  }

  return (
    <div data-testid="photo-uploader" className="space-y-3"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}>
      <div className={`grid grid-cols-3 sm:grid-cols-4 gap-2 transition-colors ${
        dragActive ? "outline outline-2 outline-oxblood/40 outline-offset-4" : ""
      }`}>
        {photos.map((p, i) => (
          <div key={p.id} data-testid={`photo-${i}`} className="relative aspect-square border border-border bg-bone group">
            <img src={fileUrl(p.url)} alt="" className="w-full h-full object-contain" />
            {p.is_primary && (
              <span className="absolute top-1 left-1 text-[9px] uppercase tracking-[0.18em] bg-oxblood text-ivory px-1.5 py-0.5">primary</span>
            )}
            <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/40 transition-colors flex items-end justify-center gap-1 pb-1.5">
              {!p.is_primary && (
                <button type="button" onClick={() => setPrimary(p.id)}
                  data-testid={`photo-${i}-primary`}
                  className="opacity-0 group-hover:opacity-100 text-[10px] uppercase tracking-[0.18em] bg-background px-2 py-1 hover:text-oxblood">
                  Make primary
                </button>
              )}
              <button type="button" onClick={() => remove(p.id)}
                data-testid={`photo-${i}-remove`}
                className="opacity-0 group-hover:opacity-100 bg-background p-1 hover:text-oxblood" aria-label="Remove">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
        {photos.length < max && (
          <button type="button" onClick={() => inputRef.current?.click()}
            data-testid="photo-add-button"
            className={`aspect-square border border-dashed flex flex-col items-center justify-center gap-2 text-xs transition-colors ${
              dragActive
                ? "border-oxblood text-oxblood bg-oxblood/5"
                : "border-border text-muted-foreground hover:border-oxblood hover:text-oxblood"
            }`}>
            <Upload className="w-5 h-5" strokeWidth={1.6} />
            {busy ? "Uploading…" : dragActive ? "Drop to upload" : "Add or drop photo"}
          </button>
        )}
        {photos.length < max && (
          <button type="button" onClick={() => cameraRef.current?.click()}
            data-testid="photo-take-photo-button"
            className="aspect-square border border-dashed border-border text-muted-foreground hover:border-oxblood hover:text-oxblood flex flex-col items-center justify-center gap-2 text-xs transition-colors">
            <Camera className="w-5 h-5" strokeWidth={1.6} />
            {busy ? "Uploading…" : "Take photo"}
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden"
        onChange={(e) => { ingest(e.target.files); e.currentTarget.value = ""; }}
        data-testid="photo-input" />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
        onChange={(e) => { ingest(e.target.files); e.currentTarget.value = ""; }}
        data-testid="photo-camera-input" />
      {error && <p data-testid="photo-error" className="text-xs text-oxblood">{error}</p>}
      <p className="text-[11px] text-muted-foreground">{photos.length} / {max} photos · 8MB max each · tap "Take photo" to snap several in a row</p>
    </div>
  );
}
